import React, { useCallback, useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, DialogTrigger, Image, Text, makeStyles, mergeClasses, shorthands, typographyStyles, useArrowNavigationGroup } from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';

import { I18nContext } from '@bingads-webui-react/i18n-context';
import { SelectableCard } from '@bingads-webui-fluent-component/selectable-card';

import microsoft from '../assets/microsoft.png';
import linkedin from '../assets/linkedin.png';

const useNetworkCardsStyles = makeStyles({
  root: {
    display: 'flex',
    marginBottom: '16px',
    ...shorthands.gap('16px'),
  },
  card: {
    width: '305px',
    height: '126px',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('8px'),
  },
  title: {
    display: 'block',
    ...typographyStyles.body1Strong,
  },
  description: {
    display: 'block',
    ...typographyStyles.caption1,
  },
  icon: {
    width: '24px',
    height: '24px',
  },
});

const Networks = [
  {
    id: 'microsoft',
    title: _TL_('Microsoft Advertising Network'),
    description: _TL_('Set up a conversion goal as you typically would.'),
    icon: microsoft,
  },
  {
    id: 'linkedin',
    title: _TL_('Compatible with LinkedIn'),
    description: _TL_('Configure a conversion goal that is compatible with LinkedIn campaigns.'),
    icon: linkedin,
  },
];

const ConfirmationDialogStrings = {
  create: {
    title: _TL_('Are you sure you want to change your settings?'),
    content: _TL_('Do you want to change the way you set up your conversion goal? Some of your conversion goal’s settings will be overwritten.'),
    confirm: _TL_('Change'),
    cancel: _TL_('Cancel'),
  },
  edit: {
    title: _TL_('Are you sure you want to make this conversion goal compatible with LinkedIn?'),
    content: _TL_('Please note that by making this conversion goal compatible with LinkedIn you will lose any conversion goal settings that are incompatible.'),
    confirm: _TL_('Yes'),
    cancel: _TL_('No'),
  },
};

export const LinkedInConfirmationDialog = ({
  open, scenario, onDismiss, onConfirm,
}) => {
  const i18n = useContext(I18nContext);

  const {
    title,
    content,
    confirm,
    cancel,
  } = useMemo(() => ConfirmationDialogStrings[scenario], [scenario]);

  return (
    <Dialog
      open={open}
      onOpenChange={onDismiss}
    >
      <DialogSurface
        className="linkedin-confirmation-dialog"
      >
        <DialogBody>
          <DialogTitle
            action={
              <DialogTrigger action="close">
                <Button
                  className="cancel-button"
                  appearance="subtle"
                  aria-label="close"
                  icon={<Dismiss24Regular />}
                />
              </DialogTrigger>
            }
          >
            {i18n.getString(title)}
          </DialogTitle>
          <DialogContent>
            {i18n.getString(content)}
          </DialogContent>
          <DialogActions>
            <Button
              className="confirm-button"
              appearance="primary"
              onClick={onConfirm}
            >
              {i18n.getString(confirm)}
            </Button>
            <Button
              className="cancel-button"
              onClick={onDismiss}
            >
              {i18n.getString(cancel)}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

LinkedInConfirmationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  scenario: PropTypes.oneOf(['create', 'edit']).isRequired,
  onDismiss: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

export const NetworkCards = ({
  scenario, selected, disabled, onChange,
}) => {
  const i18n = useContext(I18nContext);
  const classes = useNetworkCardsStyles();

  const arrowNavProps = useArrowNavigationGroup({ axis: 'horizontal' });

  const [open, setOpen] = useState(false);

  const onSelect = useCallback((newSelected) => {
    if (selected === newSelected) { return; }

    if (newSelected === 'microsoft') {
      onChange(newSelected);
      return;
    }

    setOpen(true);
  }, [selected, onChange]);

  const restoreFocus = useCallback(() => {
    const linkedInOption = document.querySelector('.linkedin-card');

    if (linkedInOption) {
      linkedInOption.focus();
    }
  }, []);

  const onConfirm = useCallback(() => {
    setOpen(false);
    restoreFocus();
    onChange('linkedin');
  }, [restoreFocus, onChange]);

  const onDismiss = useCallback(() => {
    setOpen(false);
    restoreFocus();
  }, [restoreFocus]);

  return (
    <div className={classes.root} {...arrowNavProps}>
      {Networks.map(({
        id, title, description, icon,
        }) => (
          <SelectableCard
            key={id}
            className={mergeClasses(classes.card, `${id}-card`)}
            tooltipText={(disabled && id === 'linkedin') ? i18n.getString(_TL_('You can not switch to the other type due to the LinkedIn campaign association limitations')) : undefined}
            selected={selected === id}
            disabled={disabled}
            onSelect={() => onSelect(id)}
          >
            <div className={classes.content}>
              <Image className={classes.icon} src={icon} aria-hidden="true" />
              <Text className={classes.title}>
                {i18n.getString(title)}
              </Text>
              <Text className={classes.description}>
                {i18n.getString(description)}
              </Text>
            </div>
          </SelectableCard>
      ))}
      <LinkedInConfirmationDialog
        scenario={scenario}
        open={open}
        onDismiss={onDismiss}
        onConfirm={onConfirm}
      />
    </div>
  );
};

NetworkCards.propTypes = {
  scenario: PropTypes.oneOf(['create', 'edit']).isRequired,
  selected: PropTypes.string.isRequired,
  disabled: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
};
